import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { exclusiveSelection, type DropdownChangeMeta, type Key } from 'qampo-ui-library/base';
import { DropdownList } from '../../UI/base/DropdownList';
import type { LocationResponse } from '../../../api/generated';
import styles from '../../../pages/TreatmentPlanBuilderPage.module.css';

const GLOBAL_OPTION_KEY = '__global__';
// Gap kept between the location dropdown panel and the viewport's right edge.
const LOCATION_PANEL_VIEWPORT_MARGIN = 16;

type LocationOption = { key: string; label: string };

export type TemplateLocationSelection = {
  isGlobal: boolean;
  locationIds: string[];
};

type LocationDropdownState = {
  value: Key[];
  disabledKeys: Key[];
  summary: string;
};

interface TemplateLocationDropdownProps {
  isGlobal: boolean;
  selectedLocationIds: string[];
  locations: LocationResponse[];
  onChange: (selection: TemplateLocationSelection) => void;
}

const getLocationSummary = (
  isGlobal: boolean,
  selectedLocationIds: string[],
  locations: LocationResponse[],
  t: TFunction
): string => {
  const selectedCount = selectedLocationIds.length + (isGlobal ? 1 : 0);
  if (selectedCount === 0) return t('config-builder-location-none');
  if (selectedCount > 1) return t('config-builder-location-count', { count: selectedCount });
  if (isGlobal) return t('config-builder-location-global');
  return locations.find((location) => location.id === selectedLocationIds[0])?.name ?? '';
};

const getLocationDropdownState = (
  isGlobal: boolean,
  selectedLocationIds: string[],
  options: LocationOption[],
  locations: LocationResponse[],
  t: TFunction
): LocationDropdownState => {
  const summary = getLocationSummary(isGlobal, selectedLocationIds, locations, t);
  if (isGlobal) {
    return {
      value: [GLOBAL_OPTION_KEY],
      disabledKeys: options
        .filter((opt) => opt.key !== GLOBAL_OPTION_KEY)
        .map((opt) => opt.key),
      summary
    };
  }
  return { value: selectedLocationIds, disabledKeys: [], summary };
};

export const TemplateLocationDropdown = ({
  isGlobal,
  selectedLocationIds,
  locations,
  onChange
}: TemplateLocationDropdownProps) => {
  const { t } = useTranslation();
  const fieldRef = useRef<HTMLDivElement>(null);

  // The location panel grows rightward to fit its longest option; cap it at the
  // viewport edge so a long name never pushes it off-screen.
  const [panelMaxWidth, setPanelMaxWidth] = useState<number>();
  useEffect(() => {
    const measure = () => {
      const el = fieldRef.current;
      if (!el) return;
      const { left } = el.getBoundingClientRect();
      setPanelMaxWidth(window.innerWidth - left - LOCATION_PANEL_VIEWPORT_MARGIN);
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  const options = useMemo<LocationOption[]>(
    () => [
      { key: GLOBAL_OPTION_KEY, label: t('config-builder-location-global') },
      ...locations.map((location) => ({ key: location.id, label: location.name }))
    ],
    [locations, t]
  );

  const handleChange = (next: Key[], meta: DropdownChangeMeta<LocationOption>) => {
    const resolved = exclusiveSelection(next, meta, GLOBAL_OPTION_KEY);
    const hasGlobal = resolved.includes(GLOBAL_OPTION_KEY);
    onChange({
      isGlobal: hasGlobal,
      locationIds: hasGlobal ? [] : resolved.filter((key) => key !== GLOBAL_OPTION_KEY).map(String)
    });
  };

  const { value, disabledKeys, summary } = getLocationDropdownState(
    isGlobal,
    selectedLocationIds,
    options,
    locations,
    t
  );

  return (
    <div ref={fieldRef} className={styles.locationDropdown}>
      <span className={styles.fieldLabel}>{t('config-builder-location-label')}</span>
      <DropdownList<LocationOption>
        options={options}
        getKey={(opt) => opt.key}
        renderLabel={(opt) => opt.label}
        selectionMode="multiple"
        value={value}
        disabledKeys={disabledKeys}
        onChange={handleChange}
        renderTriggerValue={() => summary}
        panelMaxWidth={panelMaxWidth}
      />
    </div>
  );
};
